import React, { useMemo, useState } from "react";
import { contextWords, openerHoldMs, sourceToTimeline, type Word } from "@hudhud/core";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { api } from "../api.js";
import { useEditor, clipHelpers } from "./store.js";
import { usePhone } from "../hooks/useMedia.js";

/** How much cut-off speech to show at each end of the clip, and how far "show more" grows it. */
const CONTEXT_MS = 8000;
const CONTEXT_STEP_MS = 10000;
const CONTEXT_MAX_MS = 120000;

/** The cut-off words at one end of the clip; clicking a word pulls it (and everything between it and the clip) back in. */
const CutOffBlock: React.FC<{ side: "before" | "after"; words: Word[]; onPick: (w: Word) => void; onMore: () => void; canMore: boolean }> = ({ side, words, onPick, onMore, canMore }) => (
  <div className="ctx-block">
    <div className="row" style={{ gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
      <span className="eyebrow" style={{ fontSize: 10 }}>{side === "before" ? "Cut off before the clip" : "Cut off after the clip"}</span>
      <span className="muted" style={{ fontSize: 11 }}>{side === "before" ? "click a word to start from it" : "click a word to end at it"}</span>
      {canMore ? <button className="chip" onClick={onMore} style={{ marginLeft: "auto" }} title={side === "before" ? "Show earlier words" : "Show later words"}><span style={{ display: "inline-flex", transform: side === "before" ? "rotate(180deg)" : "none" }}><Icon name="chev-down" size={11} /></span>{side === "before" ? "earlier" : "later"}</button> : null}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 3px", alignItems: "center" }}>
      {words.length ? words.map((w) => (
        <span key={w.startMs} className="ctx-word" role="button" tabIndex={0} onClick={() => onPick(w)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(w); } }} title={side === "before" ? `Start the clip at "${w.text}" (adds the video too)` : `End the clip after "${w.text}" (adds the video too)`}>{w.text}</span>
      )) : <span className="muted" style={{ fontSize: 12, fontStyle: "italic" }}>{side === "before" ? "nothing was cut off here" : "nothing was cut off here"}</span>}
    </div>
  </div>
);

const fmt = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}.${Math.floor((ms % 1000) / 100)}`;

export const TranscriptPane: React.FC = () => {
  const clip = useEditor((s) => s.clip);
  // which word is being said: selected from the store so the pane re-renders when the word changes, not on every frame
  const activeIdx = useEditor((s) => {
    if (!s.clip) return -1;
    const h = openerHoldMs(s.clip);
    const ws = clipHelpers.words(s.clip);
    return ws.findIndex((w) => { const t = sourceToTimeline(s.clip!.segments, w.startMs); return t != null && s.playheadMs >= t + h && s.playheadMs < t + h + (w.endMs - w.startMs) + 120; });
  });
  const selection = useEditor((s) => s.selection);
  const transcript = useEditor((s) => s.transcript);
  const { setSelection, setPlayhead, setPlaying, update, includeWord, addBack, addMusicMute } = useEditor.getState();
  const phone = usePhone();
  const [ctxMs, setCtxMs] = useState({ before: CONTEXT_MS, after: CONTEXT_MS });
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const words = useMemo(() => (clip ? clipHelpers.words(clip) : []), [clip]);
  const cardStarts = useMemo(() => new Set(clip?.captions.map((c) => c.words[0]?.startMs)), [clip]);
  const ctx = useMemo(() => (clip ? contextWords(clip, transcript?.words ?? null, ctxMs) : { before: [], after: [] }), [clip, transcript, ctxMs]);
  if (!clip) return null;
  const hold = openerHoldMs(clip);
  const tl = (w: Word) => { const t = sourceToTimeline(clip.segments, w.startMs); return t == null ? null : t + hold; };
  const sel = selection ? { a: Math.min(selection.a, selection.b), b: Math.max(selection.a, selection.b) } : null;
  const selWords = sel ? words.slice(sel.a, sel.b + 1) : [];

  const seekTo = (i: number) => { const t = tl(words[i]); if (t != null) { setPlaying(false); setPlayhead(t); } };
  const click = (i: number, e: React.MouseEvent) => {
    if (phone) {
      // a phone has no shift key: the first tap picks a word, a tap on another word stretches the selection to it, a tap inside lets go
      if (sel && i >= sel.a && i <= sel.b) { setSelection(null); seekTo(i); return; }
      if (sel) { setSelection({ a: Math.min(sel.a, i), b: Math.max(sel.b, i) }); return; }
      setSelection({ a: i, b: i }); seekTo(i); return;
    }
    if (e.shiftKey && selection) setSelection({ a: selection.a, b: i });
    else setSelection({ a: i, b: i });
    seekTo(i);
  };
  const editSelected = () => { if (!sel || sel.a !== sel.b) return; setEditing(sel.a); setDraft(words[sel.a].text); };
  const commitEdit = (i: number) => {
    const w = words[i];
    const text = draft.trim();
    setEditing(null);
    if (!text || text === w.text) return;
    update((c) => ({ ...c, edits: { ...c.edits!, wordText: { ...c.edits!.wordText, [String(w.startMs)]: text } } }));
  };
  const cutSelection = () => { if (!selWords.length) return; update((c) => ({ ...c, edits: { ...c.edits!, manualCuts: [...c.edits!.manualCuts, { inMs: selWords[0].startMs - 20, outMs: selWords[selWords.length - 1].endMs + 20 }] } })); setSelection(null); };
  const restoreSelection = () => {
    if (!selWords.length) return;
    addBack(selWords[0].startMs - 20, selWords[selWords.length - 1].endMs + 20);
  };
  const quietHere = () => { if (!selWords.length) return; addMusicMute(selWords[0].startMs - 250, selWords[selWords.length - 1].endMs + 250); };
  const toast = useToast();
  /** A picture for the selected words, made by the AI from them; opens it in the Images tab. */
  const pictureHere = async () => {
    const c = useEditor.getState().clip;
    const r = c && clipHelpers.selectedRange(c, useEditor.getState().selection);
    if (!c || !r) return;
    try {
      await useEditor.getState().flushSave();
      const added = await api.addOverlay(c.videoId, c.id, { startMs: r.startMs - 200, endMs: r.endMs + 300, subject: "", prompt: "" });
      await useEditor.getState().reloadFromServer();
      useEditor.getState().setInspectorTab("look", "pictures");
      useEditor.getState().setSelectedOverlay(added.overlay.id);
      toast("Picture added · making it now");
    } catch (e: any) { toast(e.message); }
  };
  /** Big words behind the speaker while the selected words are said: the words themselves, to start with; the cutout starts if it is not there yet. */
  const behindHere = async () => {
    const c = useEditor.getState().clip;
    const r = c && clipHelpers.selectedRange(c, useEditor.getState().selection);
    if (!c || !r) return;
    useEditor.getState().addBehindText(r.startMs - 150, r.endMs + 250, r.text.replace(/[.,!?;:"]+/g, ""));
    useEditor.getState().setInspectorTab("words", "behind");
    if (!useEditor.getState().files?.cutout) {
      try { await useEditor.getState().flushSave(); await api.cutout(c.videoId, c.id); toast("Words behind the speaker added · cutting the speaker out, a minute or two"); } catch (e: any) { toast(e.message); }
    } else toast("Words behind the speaker added");
  };
  const toggleEmphasis = () => {
    if (!selWords.length) return;
    const all = selWords.every((w) => w.emphasis);
    const keys = new Set(selWords.map((w) => w.startMs));
    update((c) => ({ ...c, words: c.words.map((w) => (keys.has(w.startMs) ? { ...w, emphasis: !all } : w)) }));
  };
  const splitCard = () => {
    if (!sel) return;
    const t = tl(words[sel.a]);
    if (t == null) return;
    update((c) => ({ ...c, edits: { ...c.edits!, cardBreaks: c.edits!.cardBreaks.includes(Math.round(t)) ? c.edits!.cardBreaks.filter((x) => x !== Math.round(t)) : [...c.edits!.cardBreaks, Math.round(t)] } }));
  };
  const startHere = () => { if (!sel) return; const w = words[sel.a]; update((c) => ({ ...c, edits: { ...c.edits!, trimStartMs: w.startMs - 150 } })); setSelection(null); };
  const endHere = () => { if (!sel) return; const w = words[sel.b]; update((c) => ({ ...c, edits: { ...c.edits!, trimEndMs: w.endMs + 250 } })); setSelection(null); };
  const toggleGap = (gap: { inMs: number; outMs: number }) => {
    const isCut = clipHelpers.gapIsCut(clip, gap);
    update((c) => {
      const e = c.edits!;
      if (isCut) return { ...c, edits: { ...e, keptGaps: [...e.keptGaps, gap], manualCuts: e.manualCuts.filter((m) => m.outMs <= gap.inMs || m.inMs >= gap.outMs) } };
      return { ...c, edits: { ...e, keptGaps: e.keptGaps.filter((k) => k.outMs <= gap.inMs || k.inMs >= gap.outMs), manualCuts: [...e.manualCuts, gap] } };
    });
  };

  const rows: React.ReactNode[] = [];
  let row: React.ReactNode[] = [];
  let rowStart: number | null = null;
  let cutRun: Word[] = [];
  const flushCutRun = () => {
    if (!cutRun.length) return;
    const run = cutRun; cutRun = [];
    const n = run.length;
    row.push(<button key={`ab${run[0].startMs}`} className="chip gold" onClick={() => addBack(run[0].startMs - 20, run[n - 1].endMs + 20)} title={`Put ${n === 1 ? "this word" : `these ${n} words`} back in, video included`}><Icon name="undo" size={11} />add back</button>);
  };
  const flushRow = () => { if (row.length) rows.push(<div key={rows.length} className="row" style={{ alignItems: "flex-start", gap: phone ? 8 : 10, padding: phone ? "6px 0" : "4px 0" }}><span className="muted" style={{ width: phone ? 38 : 44, flexShrink: 0, fontSize: 11, paddingTop: phone ? 9 : 5, fontVariantNumeric: "tabular-nums" }}>{rowStart != null ? fmt(rowStart) : "cut"}</span><div style={{ display: "flex", flexWrap: "wrap", gap: phone ? "4px 4px" : "2px 3px", alignItems: "center" }}>{row}</div></div>); row = []; rowStart = null; };
  words.forEach((w, i) => {
    const t = tl(w);
    const cut = clipHelpers.isCut(clip, w);
    if (!cut) flushCutRun();
    if (t != null && cardStarts.has(Math.round(t - hold)) && row.length) flushRow();
    if (rowStart == null && t != null) rowStart = t;
    const inSel = sel && i >= sel.a && i <= sel.b;
    const isActive = i === activeIdx;
    const style: React.CSSProperties = {
      display: "inline-block", padding: phone ? "6px 8px" : "2px 4px", borderRadius: phone ? 9 : 5, fontSize: phone ? 18 : 15, fontWeight: 600, lineHeight: phone ? "24px" : "20px", cursor: "pointer", userSelect: "none", WebkitUserSelect: "none", touchAction: "manipulation",
      color: cut ? "var(--muted)" : "var(--text)", textDecoration: cut ? "line-through" : w.emphasis ? "underline" : "none", textDecorationColor: "var(--green)", textDecorationThickness: 2, textUnderlineOffset: 3,
      opacity: cut ? 0.55 : 1, background: isActive ? "var(--gold)" : inSel ? "rgba(245,197,66,0.22)" : "transparent", ...(isActive ? { color: "#1a1300" } : {}),
    };
    row.push(editing === i ? (
      <input key={`e${i}`} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => commitEdit(i)} onKeyDown={(e) => { if (e.key === "Enter") commitEdit(i); if (e.key === "Escape") setEditing(null); }} aria-label="Caption text for this word" style={{ padding: phone ? "6px 10px" : "2px 6px", fontSize: phone ? 18 : 15, fontWeight: 600, width: Math.max(phone ? 90 : 60, draft.length * (phone ? 12 : 10)) }} />
    ) : (
      <span key={i} data-word={i} style={style} role="button" tabIndex={0} aria-pressed={!!inSel} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); click(i, e as unknown as React.MouseEvent); } }} onClick={(e) => click(i, e)} onDoubleClick={() => { setEditing(i); setDraft(w.text); }} title={phone ? undefined : "Click to seek · double-click to edit · shift-click to select a range"}>{i === 0 ? <span style={{ display: "inline-block", width: 4, height: 18, background: "var(--gold)", borderRadius: 2, verticalAlign: "middle", marginRight: 6 }} /> : null}{w.text || "·"}{i === words.length - 1 ? <span style={{ display: "inline-block", width: 4, height: 18, background: "var(--gold)", borderRadius: 2, verticalAlign: "middle", marginLeft: 6 }} /> : null}</span>
    ));
    if (cut) cutRun.push(w);
    const gap = clipHelpers.gapsAfter(clip, w, words[i + 1]);
    if (gap) {
      flushCutRun();
      const isCutGap = clipHelpers.gapIsCut(clip, gap);
      row.push(<button key={`g${i}`} className="chip" onClick={() => toggleGap(gap)} style={{ borderStyle: "dashed", color: isCutGap ? "var(--muted)" : "var(--gold)", borderColor: isCutGap ? "var(--line)" : "var(--gold)" }} title={isCutGap ? "Pause removed · click to keep it" : "Pause kept · click to cut it"}>{isCutGap ? <Icon name="scissors" size={11} /> : null}{((gap.outMs - gap.inMs) / 1000).toFixed(1)}s {isCutGap ? "cut" : "kept"}</button>);
    }
  });
  flushCutRun();
  flushRow();

  const more = (side: "before" | "after") => setCtxMs((m) => ({ ...m, [side]: Math.min(CONTEXT_MAX_MS, m[side] + CONTEXT_STEP_MS) }));
  return (
    <div className="col" style={{ height: "100%", gap: 0 }}>
      {phone ? <div className="muted" style={{ padding: "8px 14px 2px", fontSize: 12.5 }}>{sel ? (sel.a === sel.b ? "One word selected · tap another to select up to it" : `${sel.b - sel.a + 1} words selected · tap inside to let go`) : "Tap a word to select it · struck-out words are cut"}</div> : <>
        <div className="row" style={{ padding: "12px 14px 6px", gap: 8 }}>
          <span className="eyebrow">Script</span>
          <span className="muted" style={{ fontSize: 11 }}>· {words.length} words</span>
        </div>
        <div className="muted" style={{ padding: "0 14px 8px", fontSize: 11 }}>Click a word to seek · shift-click to select · double-click to edit the caption text · struck-out words are cut</div>
      </>}
      <div style={{ flex: 1, overflow: "auto", padding: phone ? "0 10px 12px" : "0 14px 10px" }}>
        <CutOffBlock side="before" words={ctx.before} onPick={(w) => includeWord(w, "before")} onMore={() => more("before")} canMore={ctxMs.before < CONTEXT_MAX_MS} />
        {rows}
        <CutOffBlock side="after" words={ctx.after} onPick={(w) => includeWord(w, "after")} onMore={() => more("after")} canMore={ctxMs.after < CONTEXT_MAX_MS} />
      </div>
      <div className={`script-actions${phone ? " phone" : ""}`} role="toolbar" aria-label="Script tools">
        <button className="btn sm soft" data-action="cut" disabled={!sel} onClick={cutSelection}><Icon name="scissors" size={14} />Cut</button>
        <button className="btn sm" disabled={!sel} onClick={restoreSelection}><Icon name="undo" size={14} />Restore</button>
        <button className="btn sm" disabled={!sel} onClick={pictureHere} title="Make a picture for these words: it shows over the video while they are spoken, and you can stretch it on the timeline"><Icon name="image" size={14} />Picture</button>
        <button className="btn sm" disabled={!sel} onClick={behindHere} title="Big words behind the speaker while these words are said (the speaker is cut out first)"><Icon name="type" size={14} />Behind</button>
        <button className="btn sm" disabled={!sel} onClick={toggleEmphasis}><Icon name="star" size={14} />Emphasis</button>
        {phone ? <button className="btn sm" disabled={!sel || sel.a !== sel.b} onClick={editSelected} title="Change the caption text of the selected word"><Icon name="type" size={14} />Edit word</button> : null}
        <button className="btn sm" disabled={!sel} onClick={splitCard}><Icon name="split" size={14} />Split card</button>
        <button className="btn sm" disabled={!sel} onClick={startHere} title="Start the clip at the selected word">Start here</button>
        <button className="btn sm" disabled={!sel} onClick={endHere} title="End the clip at the selected word">End here</button>
        <button className="btn sm" disabled={!sel} onClick={quietHere} title="Silence the BG audio while these words are spoken (Quran, du'a)"><Icon name="audio" size={14} />Quiet BG audio</button>
        {phone && sel ? <button className="btn sm quiet" onClick={() => setSelection(null)}><Icon name="x" size={14} />Clear</button> : null}
      </div>
    </div>
  );
};
